<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-clock"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Timer Setup</div>
          <div class="panel-header__sub">Configure warning and expiry timer thresholds for appointments</div>
        </div>
      </div>

      <!-- Timer sections -->
      <form @submit="onSubmit">

        <div v-for="(time, index) in timers" :key="index" class="timer-section"
          :class="index === timers.length - 1 ? 'timer-section--last' : ''">

          <div class="timer-section__header">
            <span class="type-badge"
              :class="time.warning_type === 'warning' ? 'type-badge--warning' : 'type-badge--expiry'">
              <i :class="time.warning_type === 'warning' ? 'fa fa-exclamation-triangle' : 'fa fa-times-circle'"></i>
              {{ time.warning_type === 'warning' ? 'Warning Timer' : 'Expiry Timer' }}
            </span>
            <span class="type-hint">
              {{ time.warning_type === 'warning'
                ? 'Shown when the session is approaching the time limit'
                : 'Triggered when the session has fully expired' }}
            </span>
          </div>

          <div class="field-grid">
            <div class="field-group" :class="{ 'field-group--error': errors[index].title && errors[index].title[0] }">
              <label class="field-label">Title <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="time.title" placeholder="e.g. Session Warning" />
              <span class="field-error" v-if="errors[index].title && errors[index].title[0]">{{ errors[index].title[0]
                }}</span>
            </div>

            <div class="field-group"
              :class="{ 'field-group--error': errors[index].warning_msg && errors[index].warning_msg[0] }">
              <label class="field-label">Message <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="time.warning_msg"
                :placeholder="time.warning_type === 'warning' ? 'e.g. Your session is about to expire' : 'e.g. Your session has expired'" />
              <span class="field-error" v-if="errors[index].warning_msg && errors[index].warning_msg[0]">{{
                errors[index].warning_msg[0] }}</span>
            </div>

            <div class="field-group"
              :class="{ 'field-group--error': errors[index].time_limit && errors[index].time_limit[0] }">
              <label class="field-label">Time Limit <span class="req">*</span></label>
              <div class="input-suffix">
                <input type="number" class="field-input field-input--suffixed" v-model="time.time_limit" min="1"
                  placeholder="0" />
                <span class="input-suffix__sym">min</span>
              </div>
              <span class="field-error" v-if="errors[index].time_limit && errors[index].time_limit[0]">{{
                errors[index].time_limit[0] }}</span>
            </div>
          </div>

        </div>

        <!-- Validation hint -->
        <div class="validation-note">
          <i class="fa fa-info-circle"></i>
          Warning time limit must be less than the expiry time limit
        </div>

      </form>
    </div>

    <!-- Floating save bar -->
    <div class="save-bar">
      <span class="save-bar__hint">
        <i class="fa fa-info-circle"></i> Both timers are saved together
      </span>
      <button class="save-bar__save" ref="kt_save_configure" @click="onSubmit">
        <i class="fa fa-save"></i> {{ is_edit ? 'Update' : 'Save' }} Timers
      </button>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  STORE_TIMER_SETUP,
  GET_TIMER_SETUP,
  UPDATE_TIMER_SETUP,
} from "@/core/services/store/actions.type";

export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Setting", route: "" },
        { id: 3, title: "Configure Timer", route: "" },
      ],
      errors: [
        { title: [""], warning_msg: [""], time_limit: [""] },
        { title: [""], warning_msg: [""], time_limit: [""] },
      ],
      timers: [
        { warning_type: "warning", title: "", warning_msg: "", time_limit: 5 },
        { warning_type: "expiry", title: "", warning_msg: "", time_limit: 10 },
      ],
      is_edit: 0,
    };
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.$store.dispatch(GET_TIMER_SETUP).then(data => {
      if (data.results.length > 0) this.timers = data.results;
      this.is_edit = data.results.length > 0 ? data.results[0].id : 0;
    });
  },
  methods: {
    onSubmit(e) {
      if (e && e.preventDefault) e.preventDefault();

      if (parseInt(this.timers[0].time_limit) >= parseInt(this.timers[1].time_limit)) {
        this.errors = [
          { title: [""], warning_msg: [""], time_limit: ["Warning time limit must be less than expiry time limit"] },
          { title: [""], warning_msg: [""], time_limit: [""] },
        ];
        return;
      }
      this.errors = [
        { title: [""], warning_msg: [""], time_limit: [""] },
        { title: [""], warning_msg: [""], time_limit: [""] },
      ];

      const btn = this.$refs["kt_save_configure"];
      btn.disabled = true;
      btn.classList.add("spinner", "spinner-light", "spinner-right");

      const action = this.is_edit === 0 ? STORE_TIMER_SETUP : UPDATE_TIMER_SETUP;
      this.$store.dispatch(action, { data: this.timers })
        .then(data => {
          btn.classList.remove("spinner", "spinner-light", "spinner-right");
          btn.disabled = false;
          if (this.is_edit === 0) {
            this.is_edit = data.length > 0 ? data[0].id : 0;
            this.timers = data;
          }
          this.$toastr.s(this.is_edit === 0 ? "Timer successfully added" : "Timer successfully updated");
        })
        .catch(err => {
          btn.classList.remove("spinner", "spinner-light", "spinner-right");
          btn.disabled = false;
          this.errors = err;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;
$orange: #e07b2a;
$red: #b42318;

.page-wrap {
  padding: 12px 12px 96px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: clip;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    line-height: 1.2;
  }

  &__sub {
    font-size: 12px;
    color: #7a8499;
    margin-top: 3px;
  }
}

/* ── timer section ── */
.timer-section {
  padding: 24px 28px;
  border-bottom: 1px solid $border;

  &--last {
    border-bottom: none;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;

  i {
    font-size: 11px;
  }

  &--warning {
    background: rgba(224, 123, 42, 0.1);
    color: $orange;
    border: 1px solid rgba(224, 123, 42, 0.25);
  }

  &--expiry {
    background: rgba(180, 35, 24, 0.08);
    color: $red;
    border: 1px solid rgba(180, 35, 24, 0.2);
  }
}

.type-hint {
  font-size: 12px;
  color: #7a8499;
}

/* ── fields ── */
.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 160px;
  gap: 16px;
  align-items: start;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 5px;

  &--error .field-input {
    border-color: $red;
  }
}

.field-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5a6278;
  font-family: $font;

  .req {
    color: $red;
    margin-left: 1px;
  }
}

.field-input {
  height: 38px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 11px;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  outline: none;
  background: #fff;
  width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }

  &::placeholder {
    color: #b0bac9;
  }

  &--suffixed {
    padding-right: 40px;
  }
}

.field-error {
  font-size: 11px;
  color: $red;
}

/* ── suffix ── */
.input-suffix {
  position: relative;

  &__sym {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    font-weight: 600;
    color: #7a8499;
    pointer-events: none;
  }
}

/* ── validation note ── */
.validation-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 28px 20px;
  padding: 10px 14px;
  background: #eef7fb;
  border: 1px solid #c5ddf5;
  border-radius: 8px;
  font-size: 12px;
  color: #1a6fa0;

  i {
    color: $blue;
    flex-shrink: 0;
  }
}

/* ── save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  left: 50%;
  transform: translateX(-40%);
  background: #1a2340;
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(13, 27, 62, 0.3);
  z-index: 100;

  &__hint {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.55);
    font-family: $font;

    i {
      font-size: 12px;
    }
  }

  &__save {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: $blue;
    color: #fff;
    border: none;
    border-radius: 9px;
    padding: 9px 22px;
    font-size: 13px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;

    &:hover {
      background: #2d85bb;
      box-shadow: 0 4px 14px rgba(56, 154, 212, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
      box-shadow: none;
    }

    i {
      font-size: 12px;
    }
  }
}
</style>
